import React from 'react';
import { CustomFieldDefinition } from '../../types/customField';

interface CustomFieldsRendererProps {
  customFields: CustomFieldDefinition[];
  values: { [key: string]: any };
  onChange: (key: string, value: any) => void;
  disabled?: boolean;
}

const CustomFieldsRenderer: React.FC<CustomFieldsRendererProps> = ({
  customFields,
  values,
  onChange,
  disabled = false
}) => {
  if (customFields.length === 0) {
    return null;
  }

  const renderField = (field: CustomFieldDefinition) => {
    const value = values[field.key] ?? field.defaultValue ?? '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            placeholder={field.description || `Adja meg: ${field.name}`}
            required={field.required}
            disabled={disabled}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(field.key, Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            placeholder={field.description || `Adja meg: ${field.name}`}
            required={field.required}
            disabled={disabled}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            required={field.required}
            disabled={disabled}
            style={{
              colorScheme: document.documentElement.classList.contains('dark') ? 'dark' : 'light'
            }}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            required={field.required}
            disabled={disabled}
          >
            <option value="">Válasszon...</option>
            {field.options?.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {field.options?.map(option => (
              <label
                key={option}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter(v => v !== option);
                    onChange(field.key, newValues);
                  }}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                  disabled={disabled}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {option}
                </span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(field.key, e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              disabled={disabled}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {field.description || field.name}
            </span>
          </label>
        );

      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            placeholder={field.description || `Adja meg: ${field.name}`}
            required={field.required}
            disabled={disabled}
          />
        );

      case 'phone':
        return (
          <input
            type="tel"
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            placeholder={field.description || `Adja meg: ${field.name}`}
            required={field.required}
            disabled={disabled}
          />
        );

      case 'url':
        return (
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            placeholder={field.description || `Adja meg: ${field.name}`}
            required={field.required}
            disabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {customFields.map(field => (
        <div key={field.id}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {field.name}
            {field.required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
          </label>
          {renderField(field)}
          {field.description && field.type !== 'checkbox' && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {field.description}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default CustomFieldsRenderer;
